import React, {useCallback, useLayoutEffect, useMemo, useState} from "react";
import {DefaultPaginationInfo} from "../../../../../../../core/constants/enums";
import useIsMounted from "../../../../../../hooks/use-is-mounted";
import {Paper} from "@mui/material";
import SearchInput from "../../../../../../components/base/input/search_input";
import {Tab, Tabs} from "../../../../../../components/base/Tabs";
import Api from "../../../../../../../core/services/api/api";
import Button from "../../../../../../components/base/button/button";
import {DataGridColumnAlignments, DataGridColumnTypes, useDataGridSavedState} from "../../../../../../../packages/data-grid";
import {
    PartInformationApplicationSuggestionsDataGridColumnNames,
    PartInformationTechnicalSuggestionsDataGridColumnNames
} from "../../../../../../../core/constants/table/data-grid-column-keys";
import PartnerDataGrid from "../../../../../../containers/data-grid";
import useSearch from "../../../../../../hooks/use-search";
import ProductInformationPopover from "../../../../../../components/app-specific/draggable-popover/product-information-popover";
import useQuery from "../../../../../../hooks/use-query";
import useAccessLevels from "../../../../../../hooks/use-access-levels";


const tabs = {
    pending: 'pending',
    accepted: 'accepted',
    declined: 'declined',
}

const tabsList = [
    {title: 'Pending', value: tabs.pending},
    {title: 'Accepted', value: tabs.accepted},
    {title: 'Declined', value: tabs.declined},
]

const defaultTab = tabs.pending;

const TechnicalSuggestionsView = () => {
    const [query] = useQuery()
    const [data, setData] = useState([]);
    const [updatedTab, setUpdatedTab] = useState(query.tab ?? defaultTab);
    const isMounted = useIsMounted();
    const {packages} = useAccessLevels();
    const canManageTechnicalSuggestions = packages.lookup.technicalSuggestionsManage;

    const [pendingDataGridSaveConfig, acceptedDataGridSaveConfig, declinedDataGridSaveConfig] = useMemo(() => [
        ({
            name: 'TechnicalSuggestionsViewPendingDataGrid',
            version: canManageTechnicalSuggestions ? 1 : 2,
        }),
        ({
            name: 'TechnicalSuggestionsViewAcceptedDataGrid',
            version: 1,
        }),
        ({
            name: 'TechnicalSuggestionsViewDeclinedDataGrid',
            version: 1,
        })
    ], [canManageTechnicalSuggestions])

    const columns = useMemo(() => [
        {
            title: 'Part Number',
            name: PartInformationTechnicalSuggestionsDataGridColumnNames.partNo,
            alignment: DataGridColumnAlignments.left,
            type: DataGridColumnTypes.element,
            sortable: false,
        },
        {
            title: 'Title',
            name: PartInformationTechnicalSuggestionsDataGridColumnNames.title,
            alignment: DataGridColumnAlignments.left,
            type: DataGridColumnTypes.string,
            sortable: false,
            width: 120,
        },
        {
            title: 'Type',
            name: PartInformationTechnicalSuggestionsDataGridColumnNames.type,
            alignment: DataGridColumnAlignments.left,
            type: DataGridColumnTypes.string,
            sortable: false,
        },
        {
            title: 'Default Values',
            name: PartInformationTechnicalSuggestionsDataGridColumnNames.defaultValues,
            alignment: DataGridColumnAlignments.left,
            type: DataGridColumnTypes.string,
            sortable: false,
            width: 250,
        },
        {
            title: 'Description',
            name: PartInformationTechnicalSuggestionsDataGridColumnNames.description,
            alignment: DataGridColumnAlignments.left,
            type: DataGridColumnTypes.string,
            sortable: false,
            width: 200,
        },
        ...(updatedTab === tabs.pending && canManageTechnicalSuggestions ? [
            {
                name: PartInformationApplicationSuggestionsDataGridColumnNames.actions,
                alignment: DataGridColumnAlignments.center,
                type: DataGridColumnTypes.element,
                sortable: false,
                width: 160,
            },
        ] : []),
    ], [canManageTechnicalSuggestions, updatedTab])

    const [pendingDataGridColumns, , pendingDataGridPageSize, pendingDataGridDensity] = useDataGridSavedState({
        columns: columns,
        name: pendingDataGridSaveConfig.name,
        version: pendingDataGridSaveConfig.version,
    })
    const [acceptedDataGridColumns, , acceptedDataGriPageSize, acceptedDataGridDensity] = useDataGridSavedState({
        columns: columns,
        name: acceptedDataGridSaveConfig.name,
        version: acceptedDataGridSaveConfig.version,
    })
    const [declinedDataGridColumns, , declinedDataGriPageSize, declinedDataGridDensity] = useDataGridSavedState({
        columns: columns,
        name: declinedDataGridSaveConfig.name,
        version: declinedDataGridSaveConfig.version,
    })

    const [dataGridSaveConfig, dataGridColumns, dataGridPageSize, dataGridDensity] = useMemo(() => {
        switch (updatedTab) {
            case  tabs.pending:
                return [pendingDataGridSaveConfig, pendingDataGridColumns, pendingDataGridPageSize, pendingDataGridDensity];
            case  tabs.accepted:
                return [acceptedDataGridSaveConfig, acceptedDataGridColumns, acceptedDataGriPageSize, acceptedDataGridDensity];
            case  tabs.declined:
                return [declinedDataGridSaveConfig, declinedDataGridColumns, declinedDataGriPageSize, declinedDataGridDensity];
            default:
                return [];
        }
    }, [acceptedDataGriPageSize, acceptedDataGridColumns, acceptedDataGridDensity, acceptedDataGridSaveConfig, declinedDataGriPageSize, declinedDataGridColumns, declinedDataGridDensity, declinedDataGridSaveConfig, pendingDataGridColumns, pendingDataGridDensity, pendingDataGridPageSize, pendingDataGridSaveConfig, updatedTab])

    /**
     * Searches for the application suggestions in the application
     * @type {(function(UseSearchFunctionArg, boolean=): Promise<undefined|ApiResponseDs<*>>)}
     */
    const fetchEntries = useCallback(async (args) => {
        if (!packages.lookup.technicalSuggestionsList) {
            return;
        }
        const forApi = {
            keyword: args.filters?.keyword?.toString(),
            statusId: args.tab === tabs.pending ? 1 : args.tab === tabs.accepted ? 2 : 3,
            pagination: args.paginationInfo,
        };
        const response = await Api.getTechnicalSuggestions(forApi, args.cancelTokenSource?.token);
        if (!isMounted())
            return;
        setData(response?.data?.items ?? []);
        args.setPaginationInfo(prevState => ({
            ...prevState,
            length: response?.data?.totalCount ?? 0
        }))
    }, [isMounted, packages.lookup.technicalSuggestionsList])

    /**
     * Searches among the entries based on the filters from the user.
     * @type {UseSearchFunction}
     */
    const search = useCallback(async (result) => {
        await fetchEntries(result);
    }, [fetchEntries]);

    const initialPaginationInfo = useMemo(() => dataGridPageSize ? {
        ...DefaultPaginationInfo,
        pageSize: dataGridPageSize
    } : DefaultPaginationInfo, [dataGridPageSize])

    const initialSearchDataSettings = useMemo(() => ({
        tab: updatedTab,
        paginationInfo: initialPaginationInfo,
    }), [updatedTab, initialPaginationInfo]);

    const searchData = useSearch(search, initialSearchDataSettings)

    /**
     * With each change in the [tab], updates the value of the [updatedTab] state.
     */
    useLayoutEffect(() => setUpdatedTab(searchData.tab), [searchData.tab])

    /**
     * Searches over the application suggestions based on the received keyword
     * @param keyword
     */
    const searchOverRecords = useCallback((keyword) => {
        searchData.setFilters({keyword: keyword?.trim()});
    }, [searchData])

    /**
     * Handles changes in the selected tab for status of the suggested application records
     * @param newValue
     */
    const onTabChanged = useCallback((e, newTab) => {
        setData([]);
        searchData.setTab(newTab);
        switch (newTab) {
            case tabs.pending:
                searchData.setPaginationInfo((prevState) => ({
                    ...prevState,
                    pageSize: pendingDataGridPageSize ?? initialPaginationInfo.pageSize,
                }));
                break;
            case tabs.accepted:
                searchData.setPaginationInfo((prevState) => ({
                    ...prevState,
                    pageSize: acceptedDataGriPageSize ?? initialPaginationInfo.pageSize,
                }));
                break;
            case tabs.declined:
                searchData.setPaginationInfo((prevState) => ({
                    ...prevState,
                    pageSize: declinedDataGriPageSize ?? initialPaginationInfo.pageSize,
                }));
                break;
            default:
                return;
        }
    }, [acceptedDataGriPageSize, declinedDataGriPageSize, initialPaginationInfo.pageSize, pendingDataGridPageSize, searchData])

    /**
     * Handles accepting a record of technical suggestions:
     * - Calls the api regarding this action,
     * - If the api call was successful, removes the record from the list
     */
    const onRecordAccepted = useCallback((item) => {
        if (!packages.lookup.technicalSuggestionsManage) {
            return;
        }
        searchData.setLoading(true);
        const forApi = {
            suggestionId: item.id,
            isApproved: true
        };
        Api.changeStatusOfTechnicalSuggestion(forApi).then((response) => {
            if (!isMounted())
                return;
            searchData.setLoading(false);
            if (response?.resultFlag)
                setData(prevState => prevState.filter(e => e.id !== item.id))
        })
    }, [isMounted, packages.lookup.technicalSuggestionsManage, searchData])

    /**
     * Handles declining a record of technical suggestions:
     * - Calls the api regarding this action,
     * - If the api call was successful, removes the record from the list
     */
    const onRecordDeclined = useCallback((item) => {
        if (!packages.lookup.technicalSuggestionsManage) {
            return;
        }
        const forApi = {
            suggestionId: item.id,
            isApproved: false
        };
        Api.changeStatusOfTechnicalSuggestion(forApi).then((response) => {
            if (!isMounted())
                return;
            searchData.setLoading(false);
            if (response?.resultFlag)
                setData(prevState => prevState.filter(e => e.id !== item.id))

        });
    }, [isMounted, packages.lookup.technicalSuggestionsManage, searchData])

    /**
     * Shows confirmation dialog regarding declining a suggestion
     * @param item
     */
    const getDeclinedConfirmation = useCallback((item) => {
        if (!packages.lookup.technicalSuggestionsManage) {
            return;
        }
        window.partner?.openConfirmationDialog(
            <div className={'p-3'}>
                <p className={'text-sm text-color-2'}>
                    Do you want to decline the suggested application?
                </p>
            </div>,
            {
                maxWidth: 'sm'
            },
            (v) => v && onRecordDeclined(item),
            "Decline",
            "Cancel",
        );
    }, [onRecordDeclined, packages.lookup.technicalSuggestionsManage])

    /**
     * Creates table entities
     */
    const dataGridRows = useMemo(() => {
        return data.map(e => ({
            key: e.id,
            data: e,
            cells: {
                [PartInformationTechnicalSuggestionsDataGridColumnNames.partNo]:
                    <ProductInformationPopover partNo={e.partNumber}>
                        {e.partNumber ?? ''}
                    </ProductInformationPopover>,
                [PartInformationTechnicalSuggestionsDataGridColumnNames.title]: e.title,
                [PartInformationTechnicalSuggestionsDataGridColumnNames.type]: e.type,
                [PartInformationTechnicalSuggestionsDataGridColumnNames.defaultValues]: e.values,
                [PartInformationTechnicalSuggestionsDataGridColumnNames.description]: e.description,
                ...(canManageTechnicalSuggestions ? {
                    [PartInformationTechnicalSuggestionsDataGridColumnNames.actions]:
                        <div className={'d-flex align-items-center justify-content-center'}>
                            <Button
                                variant={'outlined'}
                                color={'primary'}
                                className={'mr-3'}
                                onClick={() => onRecordAccepted(e)}
                            >
                                Accept
                            </Button>
                            <Button
                                variant={'outlined'}
                                color={'primary'}
                                onClick={() => getDeclinedConfirmation(e)}
                            >
                                Decline
                            </Button>
                        </div>,
                } : {}),
            },
        }))
    }, [canManageTechnicalSuggestions, data, getDeclinedConfirmation, onRecordAccepted])

    return (
        <>
            <>
                <Paper className='paper w-100 mt-5 flex-wrap justify-content-between align-items-start'>
                    <SearchInput
                        initialValue={searchData.filters?.keyword}
                        loading={searchData.loading}
                        autoSearch
                        immediateSearch
                        search={searchOverRecords}
                        label={'Search'}
                    />
                </Paper>
                <Tabs
                    className={'mt-4'}
                    value={searchData.tab ?? false}
                    onChange={onTabChanged}
                >
                    {
                        tabsList?.map(e => (
                            <Tab key={e.value} label={e.title} value={e.value}/>
                        ))
                    }
                </Tabs>
                <Paper className='paper w-100 px-3 pt-2 flex-wrap justify-content-between align-items-start mt-3'>
                    <PartnerDataGrid
                        {...dataGridSaveConfig}
                        initialLayoutState={{columns: columns}}
                        rows={dataGridRows}
                        columns={dataGridColumns}
                        hideExporting
                        hideFooterActions
                        state={{
                            loading: {state: searchData.loading},
                            pagination: searchData.paginationInfo,
                            density: dataGridDensity,
                        }}
                        onCurrentPageChanged={(_, current) => searchData.setPaginationInfo(prevState => ({...prevState, ...current}))}
                        onPageSizeChanged={(_, current) => searchData.setPaginationInfo(prevState => ({...prevState, ...current}))}
                    />
                </Paper>
            </>
        </>
    );
}

export default TechnicalSuggestionsView;
